/**
 * useScanPubSub - real-time PubSub for a Legislation Scan run.
 *
 * Mirrors useReviewSessionPubSub but is self-contained (no Redux): it connects
 * to the "ai_processing" hub, joins the scan's group (scan_run_uuid), and calls
 * the supplied callbacks as findings stream in and when the run completes. On
 * connect it also does a catch-up fetch, since findings emitted during the
 * connect/join window would otherwise be missed.
 */

import { useEffect, useRef } from 'react';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import { getScanFindings, ScanFinding } from '../../utils/scanApi';

const HUB = 'ai_processing';

interface ScanCompletedPayload {
  scan_run_uuid: string;
  status: string;
  findings_count?: number;
  critical?: number;
  major?: number;
  minor?: number;
  info?: number;
}

interface FindingPayload {
  scan_run_uuid: string;
  finding: ScanFinding;
}

export interface UseScanPubSubArgs {
  scanUuid: string | null;
  enabled?: boolean;
  onFinding: (finding: ScanFinding) => void;
  onCompleted?: (payload: ScanCompletedPayload) => void;
}

export function useScanPubSub({ scanUuid, enabled = true, onFinding, onCompleted }: UseScanPubSubArgs): void {
  // Keep the latest callbacks without re-subscribing.
  const onFindingRef = useRef(onFinding);
  const onCompletedRef = useRef(onCompleted);
  onFindingRef.current = onFinding;
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    if (!scanUuid || !enabled) return;

    let cancelled = false;
    const service = getMultiHubSocketService();

    const handler = (data: any, eventName: string) => {
      if (!data) return;
      // Only react to this scan's events (the hub is shared).
      if (data.scan_run_uuid && data.scan_run_uuid !== scanUuid) return;
      if (eventName === 'scan.finding_completed') {
        const p = data as FindingPayload;
        if (p.finding) onFindingRef.current(p.finding);
      } else if (eventName === 'scan.completed') {
        onCompletedRef.current?.(data as ScanCompletedPayload);
      }
    };

    const setup = async () => {
      try {
        const connected = await service.connectToHub(HUB);
        if (cancelled || !connected) return;
        service.emit(HUB, 'join', { group: scanUuid });
        service.on(HUB, '*', handler);

        // Catch-up: pull any findings already persisted before we joined.
        try {
          const { findings } = await getScanFindings(scanUuid);
          if (!cancelled && findings?.length) findings.forEach((f) => onFindingRef.current(f));
        } catch {
          // best-effort
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useScanPubSub] connect failed:', err);
      }
    };

    setup();

    return () => {
      cancelled = true;
      try {
        const svc = getMultiHubSocketService();
        svc.emit(HUB, 'leave', { group: scanUuid });
        svc.off(HUB, '*', handler);
      } catch {
        // best-effort cleanup
      }
    };
  }, [scanUuid, enabled]);
}

export default useScanPubSub;
